import React, { useState, useCallback, useEffect } from 'react';
import { Routes, Route, useParams, useNavigate, useLocation } from 'react-router-dom';
import './App.css';
import SearchBar from './components/SearchBar';
import TeamRoster from './components/TeamRoster';
import PlayerProfile from './components/PlayerProfile';
import TournamentPage from './components/TournamentPage';
import { api } from './api';

// NOTE: the force-directed player network graph (NetworkGraph.js / DetailPanel.js)
// is parked. With only national squads loaded a player's "network" is just their
// national teammates — identical to the team roster. Revisit a cross-team graph
// once Phase 2 (club careers) lands. For now the player view is a profile page.

function App() {
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/tournament" element={<HomePage />} />
      <Route path="/player/:playerId" element={<HomePage />} />
      <Route path="/team/:teamId" element={<HomePage />} />
    </Routes>
  );
}

function HomePage() {
  const { playerId, teamId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const showTournament = location.pathname === '/tournament';

  const [teamData, setTeamData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  // A profile route may carry a legacy comma-list of ids — use the first.
  const singlePlayerId = playerId ? playerId.split(',')[0] : null;

  useEffect(() => {
    if (!teamId) { setTeamData(null); return; }
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const data = await api.team(teamId);
        if (!cancelled) setTeamData(data);
      } catch (err) {
        if (!cancelled) { setError(err.message); setTeamData(null); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [teamId]);

  const handleSelectPlayer = useCallback((player) => navigate(`/player/${player.id}`), [navigate]);
  const handleSelectTeam = useCallback((team) => navigate(`/team/${team.id}`), [navigate]);
  const handleClear = useCallback(() => navigate('/'), [navigate]);

  const handleShare = useCallback(() => {
    const onMobile = 'ontouchstart' in window && window.innerWidth < 768;
    if (onMobile && navigator.share) {
      navigator.share({ title: document.title, url: window.location.href }).catch(() => {});
    } else {
      navigator.clipboard.writeText(window.location.href).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }).catch(() => {});
    }
  }, []);

  const hasContent = !!singlePlayerId || !!teamId || showTournament;

  return (
    <div className="app">
      <header className="toolbar">
        <h1 className="toolbar-title" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
          <img src="/favicon.svg" alt="" className="toolbar-logo" />
          TinyFalcon
        </h1>
        <button className={`toolbar-nav ${showTournament ? 'active' : ''}`} onClick={() => navigate('/tournament')}>
          World Cup 2026
        </button>
        <div className="toolbar-search">
          <SearchBar onSelectPlayer={handleSelectPlayer} onSelectTeam={handleSelectTeam} />
        </div>
        {hasContent && (
          <>
            <button className="toolbar-share" onClick={handleShare} title="Copy link to clipboard">
              {copied ? 'Copied!' : 'Share'}
            </button>
            <button className="toolbar-clear" onClick={handleClear}>Clear</button>
          </>
        )}
        {loading && <span className="toolbar-loading">Loading...</span>}
      </header>

      <div className="canvas">
        {error && <div className="canvas-error">Error: {error}</div>}
        {!hasContent && !error && (
          <div className="canvas-welcome">
            <h2>Search for a player or national team from the World Cup 2026</h2>
            <p>Browse squads and player profiles</p>
            <button className="welcome-cta" onClick={() => navigate('/tournament')}>
              View World Cup 2026 fixtures &amp; standings →
            </button>
          </div>
        )}
        {showTournament && <TournamentPage onSelectTeam={handleSelectTeam} />}
        {singlePlayerId && (
          <PlayerProfile
            key={singlePlayerId}
            playerId={singlePlayerId}
            onSelectPlayer={handleSelectPlayer}
            onSelectTeam={handleSelectTeam}
          />
        )}
        {teamId && teamData && (
          <TeamRoster team={teamData} onSelectPlayer={handleSelectPlayer} onSelectTeam={handleSelectTeam} />
        )}
      </div>
    </div>
  );
}

export default App;
